import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { sortBy } from "lodash";
import DraggableList from "../DraggableList/DraggableList";
import ShortCourse from "./ShortCourse";
import FooterButtons from "./FooterButtons";
import { reorder, setItemsPosition } from "../../utils/tableUtils";
import {
  updateCourse,
  addCourseSection
} from "../../redux/actions/contentCoursesActions";
import { makeGetEntities } from "../../redux/selectors/selectors";
import {
  courseSectionTypes,
  defaultShortCourseTitle
} from "../../constants/index";

const selector = makeGetEntities();

const PolicyBuilderContent = ({
  isDraggingIcon,
  versionId,
  disabled,
  onAddActivityClick,
  onEditActivityClick,
  canManageContentForPolicy
}) => {
  const { t } = useTranslation();
  const [courseSections, setCourseSections] = useState([]);
  const [acknowledgementExists, setAcknowledgementExists] = useState(false);
  const dispatch = useDispatch();
  const { versionData, isAddingCourseSection, isUpdatingCourse } = useSelector(
    ({ contentCoursesState }) => ({
      versionData: selector(contentCoursesState, {
        type: "version",
        id: versionId
      }),
      isAddingCourseSection: contentCoursesState.isAddingCourse,
      isUpdatingCourse: contentCoursesState.isUpdatingCourse
    })
  );

  /**
   * Sorts the list of sections by their position.
   * If isUpdatingCourse is true, that means the request to reorder the course sections
   * is in progress, so we don't want to update sections until that request is complete
   */
  useEffect(() => {
    if (versionData?.courseSections && !isUpdatingCourse) {
      const courseSections = sortBy(versionData.courseSections, ["position"]);
      setCourseSections([...courseSections]);
      const versionHasAcknowledgement = versionData.courseSections.some(
        section => {
          if (
            section?.shortCourseActivities &&
            section.shortCourseActivities.length > 0 &&
            section.shortCourseActivities.some(
              activity => activity.contentableType === "acknowledgement"
            )
          ) {
            return true;
          } else {
            return false;
          }
        }
      );
      setAcknowledgementExists(versionHasAcknowledgement);
    }
  }, [versionData]);

  const handleAddShortCourse = () => {
    const data = {
      title: t(defaultShortCourseTitle),
      sectionType: courseSectionTypes.mandatory
    };
    dispatch(addCourseSection(versionId, data));
  };

  const handleReorderShortCourses = reorderedSections => {
    // dropped outside the list
    if (!reorderedSections.destination) {
      return;
    }
    if (
      reorderedSections.source.index === reorderedSections.destination.index
    ) {
      return;
    }
    let reordered = reorder(
      courseSections,
      reorderedSections.source.index,
      reorderedSections.destination.index
    );
    reordered = setItemsPosition(reordered, "position");
    setCourseSections(reordered);
    dispatch(
      updateCourse(versionId, {
        ...versionData,
        courseSections: reordered
      })
    );
  };

  return (
    <>
      <DraggableList
        onDragEnd={handleReorderShortCourses}
        droppableId="short-courses"
        items={courseSections.map((courseSection, index) => ({
          render: ({ isDragging, dragHandle, isDraggingOver }) => (
            <ShortCourse
              courseSection={courseSection}
              isDraggingIcon={isDraggingIcon}
              isDraggingSection={isDragging}
              dragHandle={dragHandle}
              isDraggingOver={isDraggingOver}
              onAddActivityClick={onAddActivityClick}
              onEditActivityClick={onEditActivityClick}
              acknowledgementExists={acknowledgementExists}
              canManageContentForPolicy={canManageContentForPolicy}
            />
          ),
          draggableId: courseSection.id
        }))}
      />
      {/* While a course section is being added, we'll show a temporary short course with a loading spinner*/}
      {isAddingCourseSection && <ShortCourse showLoadingSpinner={true} />}
      <FooterButtons
        onAddShortCourse={handleAddShortCourse}
        disabled={disabled}
      />
    </>
  );
};

export default PolicyBuilderContent;

PolicyBuilderContent.propTypes = {
  /**
   * True if the user is dragging an Activity Type icon. This is used to
   * highlight the boxes in the short courses when they are dragging an icon
   */
  isDraggingIcon: PropTypes.bool,
  /**
   * The ID for the course version that the course sections belong to
   */
  versionId: PropTypes.string,
  disabled: PropTypes.bool,
  onAddActivityClick: PropTypes.func
};
